import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { handleApiError, showSuccess } from '../utils/errorHandler';
import { LogIn, Building2, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from '../contexts/I18nContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TenantLoginPage() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { t, changeLang } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantError, setTenantError] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    // Fetch tenant info
    const fetchTenantInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/tenant/${tenant}/info`);
        if (response.ok) {
          const data = await response.json();
          setTenantInfo(data);
          // Set language based on tenant's lingua setting
          if (data.lingua && ['it', 'hr', 'sl'].includes(data.lingua)) {
            changeLang(data.lingua);
          }
          if (data.stato !== 'attivo') {
            setTenantError(t('errors.tenantSuspended'));
          }
        } else {
          setTenantError(t('errors.tenantNotFound'));
        }
      } catch (error) {
        setTenantError(t('auth.connectionFailedNetwork'));
      }
    };
    
    if (tenant) {
      fetchTenantInfo();
    }
  }, [tenant, changeLang]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/tenant/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          tenant_slug: tenant
        })
      });

      let data;
      try { data = await response.json(); } catch { data = {}; }

      if (response.ok) {
        // Pulisci stato impersonazione precedente (login diretto, non via superadmin)
        localStorage.removeItem('impersonatedTenant');
        
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('tenant', JSON.stringify(data.tenant));
        localStorage.setItem('currentTenant', tenant);
        
        // Avvia il refresh automatico del token
        import('../App').then(m => m.startTokenRefresh && m.startTokenRefresh()).catch(() => {});
        
        // Sync language from the server's tenant lingua
        if (data.tenant?.lingua) {
          changeLang(data.tenant.lingua);
        }
        
        showSuccess(`${t("dashboard.welcome")}, ${data.user.nome}!`);
        // Redirect alla prima pagina visibile per l'utente
        const { getFirstVisiblePath } = await import('../config/menuConfig');
        const firstPath = getFirstVisiblePath(data.user.permissions);
        navigate(`/${tenant}/${firstPath}`);
      } else {
        handleApiError({ message: data.detail || t("auth.invalidCredentials") });
      }
    } catch (error) {
      handleApiError({ message: t("errors.connectionFailed") });
    } finally {
      setLoading(false);
    }
  };

  if (tenantError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-red-600 mb-2">{t("common.error")}</h2>
              <p className="text-gray-600 mb-4">{tenantError}</p>
              <Button variant="outline" onClick={() => navigate('/admin')}>
                Vai al pannello admin
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {tenantInfo?.logo_url ? (
            <img src={tenantInfo.logo_url} alt={tenantInfo.nome} className="h-16 mx-auto mb-4" />
          ) : (
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Building2 className="h-8 w-8 text-white" />
            </div>
          )}
          <CardTitle className="text-2xl">
            {tenantInfo?.nome || t("common.loading")}
          </CardTitle>
          <CardDescription>
            {t("auth.loginSubtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t("auth.email")}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="nome@azienda.it"
                data-testid="tenant-email-input"
                disabled={!tenantInfo || tenantInfo.stato !== 'attivo'}
              />
            </div>
            <div>
              <Label>{t("auth.password")}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="••••••••"
                data-testid="tenant-password-input"
                disabled={!tenantInfo || tenantInfo.stato !== 'attivo'}
              />
            </div>
            <Button 
              type="submit" 
              className="w-full"
              disabled={loading || !tenantInfo || tenantInfo.stato !== 'attivo'}
              data-testid="tenant-login-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.loading")}</>
              ) : (
                <><LogIn className="h-4 w-4 mr-2" /> {t("auth.login")}</>
              )}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <a href="/admin" className="text-sm text-gray-500 hover:text-gray-700">
              {t("auth.superAdminSubtitle")}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
